import t from 'react-translate';
import { css } from '@emotion/react';
import { darkGray, dividerMedium } from 'athena/styles/colors';
import {
  InputType,
  ProgramLevelQuestionType,
} from 'redux/schemas/models/mentoring-program-profile-questions';
import { textMediumFontSize } from 'styles/global_defaults/fonts';
import AthenaTextInput from 'athena/components/text-input';
import AthenaTextarea from 'athena/components/textarea';
import { SelectOption } from 'shared/components/inputs/nv-select';
import { useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { danger } from 'styles/global_defaults/colors';
import MultiSelectCheckList from './multi-select-checklist';
import ProfilePhotoField from './profile-photo-field';
import DropdownQuestion from './dropdown-question';
import TimezoneQuestion from './timezone-question';
import LocationQuestion from './location-question';

const styles = css`
  .input-label {
    color: ${darkGray};
    font-size: ${textMediumFontSize}px;
  }
  .select-dropdown {
    div {
      width: 100%;
      max-width: 626px;
      .text-medium {
        border: 1px solid ${dividerMedium};
      }
    }
    .select-menu {
      width: 100%;
    }
  }
  .required {
    .input-label::after {
      content: '*';
      color: ${danger};
    }
  }
`;

type QuestionProps = {
  id: number | string;
  type: InputType | ProgramLevelQuestionType;
  title: string;
  answer: string | string[] | null;
  name?: string;
  options?: string[];
  isRequired?: boolean;
  dataQa?: string;
  dataQaId?: string;
  ariaLabel?: string;
  renderFallbackValue?: boolean;
};

const Question = ({
  id,
  type,
  title,
  answer,
  name,
  options,
  isRequired,
  dataQa,
  dataQaId,
  ariaLabel,
  renderFallbackValue,
}: QuestionProps) => {
  const { setValue } = useFormContext();

  const initializeFormState = useCallback((value, shouldDirty, shouldTouch) => {
    setValue(
      `${id}`,
      {
        name,
        isRequired,
        mentorshipProgramProfileQuestionId: id,
        answer: value?.longName || value,
      },
      { shouldValidate: true, shouldDirty: shouldDirty || false, shouldTouch: shouldTouch || false },
    );
  }, [id, name, isRequired, setValue]);

  useEffect(() => {
    initializeFormState(answer, false, false);
  }, []);

  const handleChange = useCallback((
    value: any,
    shouldDirty: boolean = true,
    shouldTouch: boolean = true,
  ) => {
    initializeFormState(value, shouldDirty, shouldTouch);
  }, [initializeFormState]);

  const handleTextChange = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(value);
  }, [handleChange]);

  const formattedOptions = useMemo(() => options?.map((option) => ({
    label: option,
    value: option,
    dataQa: `${dataQa}_${option}`,
  })) || [], [options, dataQa]);

  const renderContent = () => {
    switch (type) {
      case InputType.IMAGE:
        return (
          <ProfilePhotoField
            title={title}
            className={isRequired ? 'required' : ''}
            onChange={handleChange}
            dataQa={dataQa}
            dataQaId={dataQaId}
          />
        );
      case InputType.SHORT_TEXT:
      case ProgramLevelQuestionType.SHORT_TEXT:
        return (
          <AthenaTextInput
            label={title}
            name={name || title}
            className={`w-100 ${isRequired ? 'required' : ''}`}
            value={answer}
            onChange={handleTextChange}
            dataQa={dataQa}
            dataQaId={dataQaId}
          />
        );
      case InputType.LONG_TEXT:
      case ProgramLevelQuestionType.LONG_TEXT:
        return (
          <AthenaTextarea
            label={title}
            name={name || title}
            className={isRequired ? 'required' : ''}
            value={answer as string}
            onChange={handleTextChange}
            dataQa={dataQa}
            dataQaId={dataQaId}
          />
        );
      case InputType.DROPDOWN:
      case ProgramLevelQuestionType.DROPDOWN:
        return (
          <DropdownQuestion
            title={title}
            answer={answer as string}
            options={formattedOptions}
            className={isRequired ? 'required' : ''}
            onChange={handleChange}
            placeholder={t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.PLACEHOLDERS.DROPDOWN()}
            dataQa={dataQa}
            dataQaId={dataQaId}
            ariaLabel={ariaLabel}
            renderFallbackValue={renderFallbackValue}
          />
        );
      case InputType.CHECKLIST:
      case ProgramLevelQuestionType.CHECKLIST:
        return (
          <MultiSelectCheckList
            label={title}
            options={formattedOptions}
            className={isRequired ? 'required' : ''}
            onChange={handleChange}
            selectedOptions={answer as string[]}
            dataQa={dataQa}
            dataQaId={dataQaId}
          />
        );
      case InputType.DISPLAY_LOCATION:
        return (
          <LocationQuestion
            title={title}
            answer={answer as string}
            isRequired={isRequired}
            dataQa={dataQa}
            dataQaId={dataQaId}
            onChange={handleChange}
          />
        );
      case InputType.TIME_ZONE:
        return (
          <TimezoneQuestion
            title={title}
            answer={answer as string}
            isRequired={isRequired}
            dataQa={dataQa}
            dataQaId={dataQaId}
            onChange={handleChange}
          />
        );
      default:
        return null;
    }
  };

  return <div css={styles} className='w-100'>{renderContent()}</div>;
};

export default Question;
